import type { SxProps, Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";

/** Shared outer container width for all public info pages (matches Learn more). */
export const PUBLIC_INFO_PAGE_MAX_WIDTH = "lg" as const;

/** Readable line length inside the card — aligned with Learn more. */
export const PUBLIC_INFO_CONTENT_MAX_WIDTH = 840;

/** Lavender accent panels — aligned with Welcome page. */
export const PUBLIC_INFO_LAVENDER_PANEL_BG = `linear-gradient(135deg, ${alpha("#6f49c9", 0.1)} 0%, ${alpha("#667eea", 0.16)} 100%)`;

export const publicInfoContentSx: SxProps<Theme> = {
  width: "100%",
  maxWidth: PUBLIC_INFO_CONTENT_MAX_WIDTH,
  mx: "auto"
};

export const publicInfoEyebrowSx: SxProps<Theme> = {
  color: "primary.main",
  fontWeight: 600,
  fontSize: { xs: "0.75rem", sm: "0.8125rem" },
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  textAlign: "center"
};

export const publicInfoPageTitleSx: SxProps<Theme> = {
  fontWeight: 800,
  color: "primary.main",
  fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
  lineHeight: 1.25,
  textAlign: "center",
  letterSpacing: "-0.02em"
};

export const publicInfoHeaderAccentSx: SxProps<Theme> = {
  width: 56,
  height: 4,
  borderRadius: 2,
  background: `linear-gradient(90deg, #6f49c9 0%, #667eea 100%)`,
  mx: "auto"
};

export const publicInfoPageIntroSx: SxProps<Theme> = {
  color: "text.secondary",
  lineHeight: 1.75,
  textAlign: "center",
  maxWidth: 560,
  mx: "auto",
  fontSize: { xs: "0.9375rem", sm: "1rem" }
};

export const publicInfoLinkSx: SxProps<Theme> = {
  fontWeight: 600,
  color: "primary.main",
  transition: "color 0.2s ease"
};

export const publicInfoSectionStackSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: { xs: 2.5, sm: 3 }
};

export const publicInfoSectionPanelSx: SxProps<Theme> = {
  p: { xs: 2, sm: 2.5 },
  borderRadius: { xs: 2, sm: 2.5 },
  background: PUBLIC_INFO_LAVENDER_PANEL_BG,
  border: `1px solid ${alpha("#6f49c9", 0.14)}`
};

export const publicInfoSectionTitleSx: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: { xs: "1rem", sm: "1.125rem" },
  color: "text.primary",
  mt: 0,
  mb: 1.25,
  letterSpacing: "-0.01em"
};

/** Section headings on User guide and Learn more — spacing to images comes from image margins. */
export const publicInfoSectionTitleWithImageSx: SxProps<Theme> = {
  ...publicInfoSectionTitleSx,
  fontSize: { xs: "1.0625rem", sm: "1.1875rem" },
  mb: 0,
  color: "primary.main"
};

/** Consistent vertical breathing room above and below hero/section images. */
export const publicInfoImageSpacingSx: SxProps<Theme> = {
  my: "1.5em"
};

export const publicInfoBodySx: SxProps<Theme> = {
  color: "text.secondary",
  lineHeight: 1.75,
  fontSize: { xs: "0.9375rem", sm: "1rem" }
};

export const publicInfoCalloutSx: SxProps<Theme> = {
  ...publicInfoBodySx,
  p: { xs: 1.75, sm: 2 },
  borderRadius: 2,
  background: alpha("#6f49c9", 0.06),
  border: `1px solid ${alpha("#6f49c9", 0.14)}`,
  borderLeft: "4px solid",
  borderLeftColor: "primary.main"
};

export const publicInfoCtaPanelSx: SxProps<Theme> = {
  mt: { xs: 3, sm: 4 },
  p: { xs: 2.25, sm: 3 },
  borderRadius: { xs: 2.5, sm: 3 },
  background: PUBLIC_INFO_LAVENDER_PANEL_BG,
  border: `1px solid ${alpha("#6f49c9", 0.18)}`,
  boxShadow: `0 8px 28px ${alpha("#1a0a3e", 0.06)}`,
  textAlign: "left"
};

export const publicInfoCtaPanelTitleSx: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: { xs: "1.0625rem", sm: "1.125rem" },
  color: "primary.main",
  mb: 1.25,
  letterSpacing: "-0.01em"
};

export const publicInfoFeatureCardSx: SxProps<Theme> = {
  p: { xs: 1.75, sm: 2 },
  borderRadius: 2,
  bgcolor: "background.paper",
  border: `1px solid ${alpha("#6f49c9", 0.12)}`,
  transition:
    "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
  height: "100%",
  "&:hover": {
    borderColor: alpha("#6f49c9", 0.35),
    boxShadow: `0 8px 24px ${alpha("#6f49c9", 0.12)}`,
    transform: "translateY(-2px)"
  }
};

export const publicInfoFeatureIconSx: SxProps<Theme> = {
  width: 40,
  height: 40,
  borderRadius: "50%",
  bgcolor: "primary.main",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mb: 1.25,
  flexShrink: 0
};

export const publicInfoGuideSectionSx: SxProps<Theme> = {
  pl: { xs: 2, sm: 2.5 },
  borderLeft: "3px solid",
  borderLeftColor: (theme) => alpha(theme.palette.primary.main, 0.3)
};

export const publicInfoOrderedListSx: SxProps<Theme> = {
  ...publicInfoBodySx,
  m: 0,
  pl: 2.5,
  listStyleType: "decimal",
  "& li": {
    mb: 1.25,
    "&:last-child": { mb: 0 },
    pl: 0.5
  }
};

export const publicInfoUnorderedListSx: SxProps<Theme> = {
  ...publicInfoBodySx,
  m: 0,
  pl: 2.5,
  listStyle: "disc",
  "& li": { mb: 1, "&:last-child": { mb: 0 } }
};

export const publicInfoFaqStackSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1.25
};

export const publicInfoFaqAccordionSx: SxProps<Theme> = {
  "&:before": { display: "none" },
  borderTopLeftRadius: 8,
  borderTopRightRadius: 8,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  bgcolor: alpha("#6f49c9", 0.05),
  border: `1px solid ${alpha("#6f49c9", 0.12)}`,
  boxShadow: "none",
  overflow: "hidden",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "&.Mui-expanded": {
    bgcolor: alpha("#6f49c9", 0.08),
    borderColor: alpha("#6f49c9", 0.22),
    boxShadow: `0 6px 20px ${alpha("#6f49c9", 0.1)}`
  }
};

export const publicInfoFaqAccordionSummarySx: SxProps<Theme> = {
  px: { xs: 1.5, sm: 2 },
  py: 1.25,
  minHeight: 52,
  "& .MuiAccordionSummary-content": {
    my: 0.5
  },
  "&:hover": {
    bgcolor: alpha("#6f49c9", 0.04)
  }
};

export const publicInfoContactStackSx: SxProps<Theme> = {
  width: "100%",
  mt: 2.5
};

export const publicInfoContactCardSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  gap: 2,
  p: { xs: 2, sm: 2.5 },
  borderRadius: 2.5,
  border: `1px solid ${alpha("#6f49c9", 0.12)}`,
  bgcolor: "background.paper",
  transition:
    "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
  "&:hover": {
    borderColor: alpha("#6f49c9", 0.35),
    boxShadow: `0 8px 24px ${alpha("#6f49c9", 0.1)}`,
    transform: "translateY(-1px)"
  }
};

export const publicInfoContactIconSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  borderRadius: "50%",
  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
  flexShrink: 0
};

export const blogHeroImageSx: SxProps<Theme> = {
  width: "100%",
  maxHeight: { xs: 220, sm: 320, md: 400 },
  objectFit: "cover",
  display: "block",
  borderRadius: { xs: 2, sm: 3 },
  boxShadow: `0 12px 32px ${alpha("#1a0a3e", 0.1)}`,
  ...publicInfoImageSpacingSx
};

export const blogSectionImageSx: SxProps<Theme> = {
  width: "100%",
  maxHeight: { xs: 200, sm: 260, md: 300 },
  objectFit: "cover",
  display: "block",
  borderRadius: 2,
  boxShadow: `0 8px 24px ${alpha("#1a0a3e", 0.08)}`,
  ...publicInfoImageSpacingSx
};

export const userGuideSectionImageSx: SxProps<Theme> = {
  ...blogSectionImageSx
};

export const userGuideSectionImageCompactSx: SxProps<Theme> = {
  width: "100%",
  maxWidth: { xs: "100%", md: 560 },
  maxHeight: { xs: 180, sm: 220, md: 240 },
  objectFit: "cover",
  display: "block",
  borderRadius: 2,
  boxShadow: `0 8px 24px ${alpha("#1a0a3e", 0.08)}`,
  ...publicInfoImageSpacingSx
};

/** Shows the full image without cropping — use for hardware/product shots. */
export const userGuideSectionImageContainSx: SxProps<Theme> = {
  width: "100%",
  maxHeight: { xs: 280, sm: 360, md: 420 },
  objectFit: "contain",
  display: "block",
  borderRadius: 2,
  boxShadow: `0 8px 24px ${alpha("#1a0a3e", 0.08)}`,
  bgcolor: alpha("#1a0a3e", 0.03),
  ...publicInfoImageSpacingSx
};
